import { ByteBuffer, InvalidInputError, InvalidRelativeValueError, } from "../ByteBuffer.js";
import { ByteString } from "../ByteString.js";
import { Ed25519 } from "../Ed25519.js";
import { NamespaceId } from "../NamespaceId.js";
import { SubspaceId } from "../SubspaceId.js";
import { UInt64 } from "../UInt64.js";
import { Willow25 } from "../Willow25.js";
import { Area } from "../groupings/Area.js";
import { PrivateAreaContext } from "../groupings/PrivateAreaContext.js";
import { PrivateInterest } from "../groupings/PrivateInterest.js";
import { CapabilitySignData } from "./CapabilitySignData.js";
import { Delegation } from "./Delegation.js";
import { NamespacePublicKey } from "./NamespacePublicKey.js";
import { NamespaceSignature } from "./NamespaceSignature.js";
import { UserPublicKey } from "./UserPublicKey.js";
import { UserSignature } from "./UserSignature.js";
export var CapabilityAccessMode;
(function (CapabilityAccessMode) {
    CapabilityAccessMode[CapabilityAccessMode["READ"] = 0] = "READ";
    CapabilityAccessMode[CapabilityAccessMode["WRITE"] = 1] = "WRITE";
})(CapabilityAccessMode || (CapabilityAccessMode = {}));
export class Capability {
    accessMode;
    namespaceKey;
    userKey;
    delegations;
    initialAuthorisation;
    static is(value) {
        return !!(value &&
            typeof value === "object" &&
            "accessMode" in value &&
            (value.accessMode === CapabilityAccessMode.READ ||
                value.accessMode === CapabilityAccessMode.WRITE) &&
            "namespaceKey" in value &&
            NamespacePublicKey.is(value.namespaceKey) &&
            "userKey" in value &&
            UserPublicKey.is(value.userKey) &&
            "delegations" in value &&
            Array.isArray(value.delegations) &&
            value.delegations.every((val) => Delegation.is(val)) &&
            (!("initialAuthorisation" in value) ||
                value.initialAuthorisation === undefined ||
                NamespaceSignature.is(value.initialAuthorisation)));
    }
    static isCommunal(val) {
        return val.initialAuthorisation === undefined;
    }
    static isOwned(val) {
        return NamespaceSignature.is(val.initialAuthorisation);
    }
    static async isValid(val) {
        if (val.delegations.length > Willow25.MAX_CAPABILITY_DELEGATIONS_COUNT) {
            throw new Error("Got too many delegations in val: " +
                val.delegations.length +
                "; max is " +
                Willow25.MAX_CAPABILITY_DELEGATIONS_COUNT, {
                cause: val,
            });
        }
        const isValOwned = this.isOwned(val);
        const isNamespaceOwned = NamespacePublicKey.isOwned(val.namespaceKey);
        if ((!isValOwned && isNamespaceOwned) || (isValOwned && !isNamespaceOwned)) {
            return false;
        }
        const finalDelegation = this.getFinalDelegation(val);
        if (finalDelegation === undefined) {
            if (isValOwned) {
                return await Ed25519.verify(val.namespaceKey, val.initialAuthorisation, await ByteBuffer.produceEncoded(val, this.encodeOwnedInitialPayload));
            }
            else {
                return true;
            }
        }
        const previousCapability = {
            accessMode: val.accessMode,
            namespaceKey: val.namespaceKey,
            userKey: val.userKey,
            delegations: val.delegations.slice(0, val.delegations.length - 1),
            initialAuthorisation: isNamespaceOwned ? val.initialAuthorisation : undefined,
        };
        const previousArea = this.getGrantedArea(previousCapability);
        const previousReceiver = this.getReceiver(previousCapability);
        const newArea = this.getGrantedArea(val);
        const newReceiver = this.getReceiver(val);
        return (Delegation.isValid(finalDelegation) &&
            Area.includes(previousArea, newArea) &&
            (await this.isValid(previousCapability)) &&
            (await Ed25519.verify(previousReceiver, finalDelegation.userSignature, await ByteBuffer.produceEncoded({ previousCapability, newArea, newReceiver }, this.encodeHandoverPayload))));
    }
    static equals(a, b) {
        return (a.accessMode === b.accessMode &&
            a.delegations.length === b.delegations.length &&
            a.delegations.every((aDelegation, index) => Delegation.equals(aDelegation, b.delegations[index])) &&
            NamespaceSignature.equals(a.initialAuthorisation, b.initialAuthorisation) &&
            NamespacePublicKey.equals(a.namespaceKey, b.namespaceKey) &&
            UserPublicKey.equals(a.userKey, b.userKey));
    }
    static copy(val) {
        return {
            accessMode: val.accessMode,
            namespaceKey: NamespacePublicKey.copy(val.namespaceKey),
            userKey: UserPublicKey.copy(val.userKey),
            delegations: val.delegations.map((delegation) => Delegation.copy(delegation)),
            initialAuthorisation: val.initialAuthorisation === undefined
                ? undefined
                : UserSignature.copy(val.initialAuthorisation),
        };
    }
    static default() {
        return {
            accessMode: CapabilityAccessMode.WRITE,
            namespaceKey: NamespaceId.default(),
            userKey: SubspaceId.default(),
            delegations: [],
        };
    }
    static async create(accessMode, namespaceKey, userKey, signData) {
        const output = {
            accessMode,
            namespaceKey,
            userKey,
            delegations: signData?.delegations ?? [],
            initialAuthorisation: undefined,
        };
        if (NamespaceId.isOwned(namespaceKey)) {
            if (!signData) {
                throw new Error("To create an owned capability, signData is required");
            }
            output.initialAuthorisation =
                signData.initialAuthorisation ??
                    (await Ed25519.sign(signData.secretKey, await ByteBuffer.produceEncoded({ accessMode, userKey }, this.encodeOwnedInitialPayload)));
        }
        return output;
    }
    static getGrantedArea(val) {
        const finalDelegation = this.getFinalDelegation(val);
        if (finalDelegation) {
            return finalDelegation.area;
        }
        else {
            return this.isCommunal(val) ? Area.ofSubspace(val.userKey) : Area.full();
        }
    }
    static getReceiver(val) {
        const finalDelegation = this.getFinalDelegation(val);
        return finalDelegation ? finalDelegation.userPublicKey : val.userKey;
    }
    static getGrantedNamespace(val) {
        return val.namespaceKey;
    }
    static getFinalDelegation(val) {
        return val.delegations[val.delegations.length - 1];
    }
    static async delegate(val, secretKey, newReceiver, newArea = this.getGrantedArea(val), ignoreInvalidResult = false) {
        const newCapability = this.copy(val);
        newCapability.delegations.push({
            area: newArea,
            userPublicKey: newReceiver,
            userSignature: await Ed25519.sign(secretKey, await ByteBuffer.produceEncoded({ previousCapability: val, newArea, newReceiver }, this.encodeHandoverPayload)),
        });
        if (!ignoreInvalidResult && !(await this.isValid(newCapability))) {
            throw new Error("resulting capability is invalid", {
                cause: newCapability,
            });
        }
        return newCapability;
    }
    static async encode(val, consumer) {
        const isOwned = Capability.isOwned(val);
        let headerByte = 0b0000_0000;
        if (isOwned) {
            headerByte |= 0b1000_0000;
        }
        if (val.accessMode === CapabilityAccessMode.WRITE) {
            headerByte |= 0b0100_0000;
        }
        const delegationsLength = UInt64.encodeCompact(BigInt(val.delegations.length), headerByte, 6, 2);
        headerByte = delegationsLength.headerByte;
        await consumer.write(headerByte);
        await NamespacePublicKey.encode(val.namespaceKey, consumer);
        await UserPublicKey.encode(val.userKey, consumer);
        if (isOwned) {
            await NamespaceSignature.encode(val.initialAuthorisation, consumer);
        }
        await consumer.write(delegationsLength.additionalBytes);
        let previousArea = isOwned ? Area.full() : Area.ofSubspace(val.userKey);
        for (const delegation of val.delegations) {
            await Area.encodeAreaInArea(delegation.area, previousArea, consumer);
            await UserPublicKey.encode(delegation.userPublicKey, consumer);
            await UserSignature.encode(delegation.userSignature, consumer);
            previousArea = delegation.area;
        }
    }
    static async decode(producer, canonic) {
        const headerByte = (await producer.read(1))[0];
        const isOwned = !!(headerByte & 0b1000_0000);
        const isWrite = !!(headerByte & 0b0100_0000);
        const namespaceKey = await NamespacePublicKey.decode(producer);
        if (!isOwned !== NamespaceId.isCommunal(namespaceKey)) {
            throw new InvalidInputError("Invalid namespacePublicKey for " +
                (isOwned ? "owned" : "communal") +
                " capability", {
                cause: namespaceKey,
            });
        }
        const userKey = await UserPublicKey.decode(producer);
        let initialAuthorisation;
        if (isOwned) {
            initialAuthorisation = await NamespaceSignature.decode(producer);
        }
        const delegationsLength = await UInt64.decodeCompact(headerByte, 6, 2, producer, canonic);
        if (delegationsLength > Willow25.MAX_CAPABILITY_DELEGATIONS_COUNT) {
            throw new InvalidInputError("delegationsLength is too big: " +
                delegationsLength +
                "; max is " +
                Willow25.MAX_CAPABILITY_DELEGATIONS_COUNT);
        }
        const delegations = [];
        let previousArea = isOwned ? Area.full() : Area.ofSubspace(userKey);
        for (let i = 0; i < delegationsLength; i++) {
            const delegation = {
                area: await Area.decodeAreaInArea(previousArea, producer, canonic),
                userPublicKey: await UserPublicKey.decode(producer),
                userSignature: await UserSignature.decode(producer),
            };
            delegations.push(delegation);
            previousArea = delegation.area;
        }
        const result = {
            namespaceKey,
            userKey,
            accessMode: isWrite ? CapabilityAccessMode.WRITE : CapabilityAccessMode.READ,
            delegations,
            initialAuthorisation,
        };
        if (!(await Capability.isValid(result))) {
            throw new InvalidInputError("Got invalid result", { cause: result });
        }
        return result;
    }
    static async encodeHandoverPayload({ previousCapability, newArea, newReceiver, }, consumer) {
        const previousDelegation = Capability.getFinalDelegation(previousCapability);
        const previousArea = Capability.getGrantedArea(previousCapability);
        const previousSignature = previousDelegation === undefined
            ? previousCapability.initialAuthorisation
            : previousDelegation.userSignature;
        if (previousSignature === undefined) {
            const accessModeByte = previousCapability.accessMode === CapabilityAccessMode.WRITE
                ? ByteString.of(0x01)
                : ByteString.of(0x00);
            await consumer.write(accessModeByte);
            await NamespacePublicKey.encode(previousCapability.namespaceKey, consumer);
            await Area.encodeAreaInArea(newArea, previousArea, consumer);
        }
        else {
            await Area.encodeAreaInArea(newArea, previousArea, consumer);
            if (Capability.isOwned(previousCapability)) {
                await NamespaceSignature.encode(previousSignature, consumer);
            }
            else {
                await UserSignature.encode(previousSignature, consumer);
            }
        }
        await UserPublicKey.encode(newReceiver, consumer);
    }
    static async encodeRelative(val, rel, consumer) {
        if (val.accessMode !== CapabilityAccessMode.WRITE) {
            throw new Error("val.accessMode must be WRITE", {
                cause: val,
            });
        }
        if (rel.priorCap.accessMode !== CapabilityAccessMode.WRITE) {
            throw new Error("rel.priorCap.accessMode must be WRITE", {
                cause: rel,
            });
        }
        if (!NamespacePublicKey.equals(val.namespaceKey, rel.entry.namespaceId)) {
            throw new Error("val and rel.entry must have equal namespaces", {
                cause: {
                    val,
                    relEntry: rel.entry,
                },
            });
        }
        if (!Area.includesEntry(this.getGrantedArea(val), rel.entry)) {
            throw new Error("entry must be included in val's granted area", {
                cause: {
                    val,
                    relEntry: rel.entry,
                },
            });
        }
        const isValOwned = this.isOwned(val);
        const isValCommunal = !isValOwned;
        const isRelCommunal = this.isCommunal(rel.priorCap);
        const isRelative = (isValCommunal && isRelCommunal) ||
            (isValOwned &&
                !isRelCommunal &&
                UserPublicKey.equals(val.userKey, rel.priorCap.userKey) &&
                NamespaceSignature.equals(val.initialAuthorisation, rel.priorCap.initialAuthorisation));
        const shared = isRelative
            ? BigInt(Delegation.getLongestCommonPrefixLength(val, rel.priorCap))
            : 0n;
        const niceHack = isRelative ? shared + 1n : 0n;
        let headerByte = 0b0000_0000;
        if (isValOwned) {
            headerByte |= 0b1000_0000;
        }
        const encodedNiceHack = UInt64.encodeCompact(niceHack, headerByte, 3, 1);
        headerByte = encodedNiceHack.headerByte;
        const encodedDelegationsLength = UInt64.encodeCompact(BigInt(val.delegations.length), headerByte, 4, 4);
        headerByte = encodedDelegationsLength.headerByte;
        await consumer.write(headerByte);
        await consumer.write(encodedNiceHack.additionalBytes);
        await consumer.write(encodedDelegationsLength.additionalBytes);
        if (isValOwned && !isRelative) {
            await UserPublicKey.encode(val.userKey, consumer);
            await NamespaceSignature.encode(val.initialAuthorisation, consumer);
        }
        const ctxPrivateInterest = {
            namespaceId: rel.entry.namespaceId,
            subspaceId: rel.entry.subspaceId,
            path: rel.entry.path,
        };
        for (let i = Number(shared); i < val.delegations.length; i++) {
            const delegation = val.delegations[i];
            const previousCtx = {
                privateInterest: ctxPrivateInterest,
                rel: i === 0 ? Area.ofSubspace(rel.entry.subspaceId) : val.delegations[i - 1].area,
            };
            await PrivateAreaContext.encodePrivateAreaAlmostInArea(delegation.area, previousCtx, consumer);
            await UserPublicKey.encode(delegation.userPublicKey, consumer);
            await UserSignature.encode(delegation.userSignature, consumer);
        }
    }
    static async decodeRelative(rel, producer) {
        if (rel.priorCap.accessMode !== CapabilityAccessMode.WRITE) {
            throw new Error("rel.priorCap.accessMode must be WRITE", {
                cause: rel,
            });
        }
        const headerByte = (await producer.read(1))[0];
        const isValOwned = !!(headerByte & 0b1000_0000);
        const niceHack = await UInt64.decodeCompact(headerByte, 3, 1, producer, false);
        const delegationsLength = await UInt64.decodeCompact(headerByte, 4, 4, producer, false);
        if (delegationsLength > Willow25.MAX_CAPABILITY_DELEGATIONS_COUNT) {
            throw new InvalidInputError("delegationsLength is too big: " +
                delegationsLength +
                "; max is " +
                Willow25.MAX_CAPABILITY_DELEGATIONS_COUNT);
        }
        const isRelative = niceHack !== 0n;
        if (isValOwned && isRelative && Capability.isCommunal(rel.priorCap)) {
            throw new InvalidInputError("niceHack is not 0 and rel.priorCap is communal: val must be communal", {
                cause: {
                    isValOwned,
                    niceHack,
                    relPriorCap: rel.priorCap,
                },
            });
        }
        const shared = niceHack - 1n;
        if (shared > rel.priorCap.delegations.length) {
            throw new InvalidInputError("shared must be <= to rel.priorCap.delegations.length", {
                cause: {
                    niceHack,
                    shared,
                    relPriorCap: rel.priorCap,
                },
            });
        }
        if (!isRelative && shared > 0) {
            throw new InvalidInputError("when niceHack is 0, shareLength must also be 0", {
                cause: {
                    niceHack,
                    shared,
                },
            });
        }
        if (delegationsLength < shared) {
            throw new InvalidInputError("delegationsLength must be >= shared", {
                cause: {
                    shared,
                    delegationsLength,
                },
            });
        }
        let userKey;
        let initialAuthorisation;
        if (isValOwned) {
            if (isRelative) {
                userKey = rel.priorCap.userKey;
                initialAuthorisation = rel.priorCap.initialAuthorisation;
            }
            else {
                userKey = await UserPublicKey.decode(producer);
                initialAuthorisation = await NamespaceSignature.decode(producer);
            }
        }
        else {
            userKey = rel.entry.subspaceId;
            initialAuthorisation = undefined;
        }
        const delegations = [];
        const ctxPrivateInterest = {
            namespaceId: rel.entry.namespaceId,
            subspaceId: rel.entry.subspaceId,
            path: rel.entry.path,
        };
        for (let i = 0; i < delegationsLength; i++) {
            let delegation;
            if (i < shared) {
                delegation = Delegation.copy(rel.priorCap.delegations[i]);
            }
            else {
                const previousCtx = {
                    privateInterest: ctxPrivateInterest,
                    rel: i === 0 ? Area.ofSubspace(rel.entry.subspaceId) : delegations[i - 1].area,
                };
                let area;
                try {
                    area = await PrivateAreaContext.decodePrivateAreaAlmostInArea(previousCtx, producer);
                }
                catch (e) {
                    if (e instanceof InvalidRelativeValueError) {
                        throw new InvalidInputError("Got a delegation whose area is not almost included in previous private interest", { cause: e });
                    }
                    else {
                        throw e;
                    }
                }
                const userPublicKey = await UserPublicKey.decode(producer);
                const userSignature = await UserSignature.decode(producer);
                delegation = {
                    area,
                    userPublicKey,
                    userSignature,
                };
            }
            if (!Area.includesEntry(delegation.area, rel.entry)) {
                throw new InvalidInputError("Got a delegation whose area doesn't include rel.entry", {
                    cause: {
                        delegation,
                        relEntry: rel.entry,
                    },
                });
            }
            delegations.push(delegation);
        }
        const result = {
            accessMode: CapabilityAccessMode.WRITE,
            namespaceKey: rel.entry.namespaceId,
            userKey,
            delegations,
            initialAuthorisation,
        };
        if (!(await this.isValid(result))) {
            throw new InvalidInputError("Got invalid result", {
                cause: result,
            });
        }
        return result;
    }
    static async encodeOwnedInitialPayload({ accessMode, userKey }, consumer) {
        await consumer.write(accessMode === CapabilityAccessMode.READ ? 0x02 : 0x03);
        await UserPublicKey.encode(userKey, consumer);
    }
    constructor(accessMode, namespaceKey, userKey, delegations, initialAuthorisation) {
        this.accessMode = accessMode;
        this.namespaceKey = namespaceKey;
        this.userKey = userKey;
        this.delegations = delegations;
        this.initialAuthorisation = initialAuthorisation;
    }
}
//# sourceMappingURL=Capability.js.map